import type {GridState, SerializedCellCoordinates} from 'shared/ui-widgets/grid/types';
import type {GridField, ScalarGridValue, SimpleGridFieldValue} from 'src/features/record-grid/lib/types';
import type {Table} from '@tanstack/svelte-table';
import {getCellsBoundingRect, getCellValueByCoordinates, parseCellCoordinates} from 'shared/ui-widgets/grid/utils';
import {isErrorGridFieldValue, isScalarGridValue} from 'src/features/record-grid/lib/types-utils';
import {isNumber} from 'shared/utils/number-utils';
import {exists} from 'shared/utils/custom-utils';
import {DateTime} from 'luxon';

export type AggregationFunctionType = 'sum' | 'avg' | 'max' | 'min' | 'date-min' | 'date-max';

interface SelectedCounts {
    rows: number;
    columns: number;
    cells: number;
}

export interface AggregationResult {
    counts: SelectedCounts;
    numbers: {
        totalCount: number;
        sum: number;
        avg: number;
        max: number;
        min: number;
    },
    dates: {
        totalCount: number;
        min: string;
        max: string;
    },
    anyErrorsInSelection: boolean;
}

export function getAggregationResult<ITEM>(state: GridState, table: Table<ITEM>): AggregationResult {
    const flattenedSelectedCellValues = getFlattenedSelectedCellValues(state, table);

    const numberCellValues = flattenedSelectedCellValues
        .filter((field) => isScalarGridValue(field, isNumber))

    const dateCellValues = flattenedSelectedCellValues
        .filter((field) => isScalarGridValue(field, (val: unknown): val is string => {
            if (typeof val !== 'string') {
                return false;
            }

            const parsedDate = DateTime.fromISO(val);
            return parsedDate.isValid;
        }));

    const errorCellValues = flattenedSelectedCellValues.filter((field) => isErrorGridFieldValue(field));

    return {
        counts: getSelectedCounts(state),
        numbers: {
            totalCount: numberCellValues.length,
            sum: computeSumFromSelectedCells(numberCellValues),
            avg: computeAvgFromSelectedCells(numberCellValues),
            max: computeMaxFromSelectedCells(numberCellValues),
            min: computeMinFromSelectedCells(numberCellValues)
        },
        dates: {
            totalCount: dateCellValues.length,
            min: getMinDateFromSelectedCells(dateCellValues),
            max: getMaxDateFromSelectedCells(dateCellValues)
        },
        anyErrorsInSelection: errorCellValues.length > 0
    }
}

function getSelectedCounts(state: GridState): SelectedCounts {
    const allSelectedCoords = getAllSelectedCoords(state);

    const {minRow, maxRow, minCol, maxCol} = getCellsBoundingRect(allSelectedCoords);
    const rows = Math.max(maxRow - minRow + 1, 0);
    const columns = Math.max(maxCol - minCol + 1, 0);

    return {
        rows,
        columns,
        cells: allSelectedCoords.size
    };
}


function computeSumFromSelectedCells(selectedNumberCellValues: ScalarGridValue<number>[]): number {
    return selectedNumberCellValues.reduce((acc, field) => acc + field.value, 0);
}

function computeAvgFromSelectedCells(selectedNumberCellValues: ScalarGridValue<number>[]): number {
    const filteredValues = selectedNumberCellValues.map((field) => field.value);

    if (filteredValues.length === 0) {
        return 0;
    }

    return filteredValues.reduce((acc, value) => acc + value, 0) / filteredValues.length;
}

function computeMaxFromSelectedCells(selectedNumberCellValues: ScalarGridValue<number>[]): number {
    const result = selectedNumberCellValues.reduce((acc, field) => Math.max(acc, field.value), -Infinity);

    if (result === -Infinity) {
        return 0;
    }

    return result;
}

function computeMinFromSelectedCells(selectedNumberCellValues: ScalarGridValue<number>[]): number {
    const result = selectedNumberCellValues.reduce((acc, field) => Math.min(acc, field.value), Infinity);

    if (result === Infinity) {
        return 0;
    }

    return result;
}

function getMaxDateFromSelectedCells(selectedDateCellValues: ScalarGridValue<string>[]): string {
    const result = selectedDateCellValues.reduce((acc, field) => {
        const parsedAcc = DateTime.fromISO(acc);
        const parsedField = DateTime.fromISO(field.value);
        return parsedAcc > parsedField ? acc : field.value;
    }, '0001-01-01T00:00:00.000Z');

    if (result === '0001-01-01T00:00:00.000Z') {
        return '';
    }

    return result;
}

function getMinDateFromSelectedCells(selectedDateCellValues: ScalarGridValue<string>[]): string {
    const result = selectedDateCellValues.reduce((acc, field) => {
        const parsedAcc = DateTime.fromISO(acc);
        const parsedField = DateTime.fromISO(field.value);
        return parsedAcc < parsedField ? acc : field.value;
    }, '9999-12-31T23:59:59.999Z');

    if (result === '9999-12-31T23:59:59.999Z') {
        return '';
    }

    return result;
}

function getFlattenedSelectedCellValues<ITEM>(state: GridState, table: Table<ITEM>): SimpleGridFieldValue[] {
    const allSelectedCoords = getAllSelectedCoords(state);
    const parsedSelectedCoords = [...allSelectedCoords].map(parseCellCoordinates);
    const selectedCellsValues = parsedSelectedCoords.map((coordinates) => {
        try {
            return getCellValueByCoordinates<ITEM, GridField<SimpleGridFieldValue>[]>(table, coordinates)
        } catch {
            return [];
        }
    });
    return selectedCellsValues.flatMap((cellWithFieldRepetition) => cellWithFieldRepetition); // flatten field repetitions
}

function getAllSelectedCoords(state: GridState): Set<SerializedCellCoordinates> {
    const selectedCellCoordinates = [...state.selected.allSelected];
    if (exists(state.cursor.physicalCursor)) {
        selectedCellCoordinates.push(state.cursor.physicalCursor.serialize());
    }

    return new Set(selectedCellCoordinates);
}